import React, { useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  Platform,
  StyleProp,
  ViewStyle,
} from 'react-native';

import styles from './fragment.style';
import { useColors } from '../../hooks';
import { useKeyPress } from '../../hooks/use-key-press';
import ThemedText2 from '../themed-text-2';

export type FragmentOptionType = {
  text: string;
  key: number;
  isWrong?: boolean;
} | null;

interface FragmentProps {
  onPress: (option: FragmentOptionType) => void;
  option: FragmentOptionType;
  type: 'option' | 'answer-primary' | 'answer-light';
  isSelected?: boolean;
  isErrorVisible?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  placeholderStyle?: StyleProp<ViewStyle>;
  fragmentStyle?: StyleProp<ViewStyle>;
}

export default function Fragment({
  onPress,
  option,
  type,
  containerStyle,
  placeholderStyle,
  fragmentStyle,
  isSelected = false,
  isErrorVisible = false,
}: FragmentProps) {
  const colors = useColors();
  const optionKey = option ? `${(option.key + 1) % 10}` : undefined;
  const keyPress = useKeyPress(optionKey);

  useEffect(() => {
    if (option && keyPress) {
      onPress(option);
    }
  }, [option, keyPress, onPress]);

  if (!option) {
    return (
      <View
        style={[
          styles.placeholder,
          placeholderStyle,
          type === 'answer-primary' && [
            {
              backgroundColor: colors.primaryOpacity,
              borderColor: colors.onPrimary,
              borderStyle: 'dashed',
            },
          ],
          type === 'answer-light' && [
            {
              backgroundColor: colors.offWhite,
              borderColor: colors.lightSilverGray,
              borderStyle: 'dashed',
            },
          ],
          type === 'option' && {
            backgroundColor: colors.offWhite,
            borderColor: colors.borderColor,
          },
          isSelected && {
            borderColor: colors.primary,
            backgroundColor: colors.primaryLight,
          },
        ]}
      />
    );
  }

  return (
    <TouchableOpacity
      activeOpacity={0.6}
      style={[styles.container, containerStyle]}
      onPress={() => onPress(option)}
    >
      <View
        style={[
          styles.fragment,
          fragmentStyle,
          {
            backgroundColor: colors.background,
            borderColor: colors.borderColor,
          },
          option.isWrong &&
            isErrorVisible && {
              borderColor: colors.error,
            },
        ]}
      >
        <ThemedText2 type="gray" style={styles.fragmentText}>
          {option.text}
        </ThemedText2>
      </View>
      {Platform.OS === 'web' && (
        <View style={styles.optionKeyContainer}>
          <View
            style={[
              styles.optionKey,
              {
                backgroundColor: colors.background,
                borderColor: colors.borderColor,
              },
            ]}
          >
            <ThemedText2 fontWeightStyle="bold">
              {(option.key + 1) % 10}
            </ThemedText2>
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
}
